/**
 * LoadingFact.tsx (exporté sous LoadingQuiz pour compatibilité)
 * ─────────────────────────────────────────────────────────────
 * Affiche un "Le saviez-vous ?" juridique pendant la recherche.
 * Défile automatiquement toutes les 4s.
 */

import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

// Les textes sont traduits via jurisprudence.quiz.fact_{index}
const FACTS = [
  { emoji: "⚖️", factKey: 'jurisprudence.quiz.fact_0' },
  { emoji: "📜", factKey: 'jurisprudence.quiz.fact_1' },
  { emoji: "🔁", factKey: 'jurisprudence.quiz.fact_2' },
  { emoji: "📅", factKey: 'jurisprudence.quiz.fact_3' },
  { emoji: "🏛️", factKey: 'jurisprudence.quiz.fact_4' },
  { emoji: "💡", factKey: 'jurisprudence.quiz.fact_5' },
  { emoji: "🔍", factKey: 'jurisprudence.quiz.fact_6' },
  { emoji: "📋", factKey: 'jurisprudence.quiz.fact_7' },
  { emoji: "🌍", factKey: 'jurisprudence.quiz.fact_8' },
  { emoji: "⚡", factKey: 'jurisprudence.quiz.fact_9' },
];

export const LoadingQuiz = () => {
  const { t } = useTranslation();
  const [index,   setIndex]   = useState(() => Math.floor(Math.random() * FACTS.length));
  const [visible, setVisible] = useState(true);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    timerRef.current = setInterval(() => {
      // Fade out
      setVisible(false);
      setTimeout(() => {
        setIndex(i => (i + 1) % FACTS.length);
        setVisible(true);
      }, 400);
    }, 4000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  const fact = FACTS[index];

  return (
    <div className="flex flex-col items-center justify-center py-20 gap-6">

      {/* Spinner */}
      <div className="flex items-center gap-3">
        <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-muted-foreground">{t('jurisprudence.quiz.searching')}</p>
      </div>

      {/* Card fait juridique */}
      <div
        className="w-full max-w-lg rounded-2xl border bg-card px-6 py-5 shadow-sm"
        style={{
          opacity:    visible ? 1 : 0,
          transform:  visible ? 'translateY(0)' : 'translateY(6px)',
          transition: 'opacity 0.35s ease, transform 0.35s ease',
        }}
      >
        <p className="text-xs font-semibold text-primary uppercase tracking-widest mb-3">
          {t('jurisprudence.quiz.did_you_know')}
        </p>
        <div className="flex items-start gap-3">
          <span className="text-2xl shrink-0 mt-0.5">{fact.emoji}</span>
          <p className="text-sm text-foreground leading-relaxed">{t(fact.factKey)}</p>
        </div>
      </div>

      {/* Dots de progression */}
      <div className="flex gap-1.5">
        {FACTS.map((_, i) => (
          <div
            key={i}
            className="rounded-full transition-all duration-300"
            style={{
              width:      i === index ? '16px' : '6px',
              height:     '6px',
              background: i === index ? 'hsl(var(--primary))' : 'hsl(var(--muted-foreground) / 0.3)',
            }}
          />
        ))}
      </div>
    </div>
  );
};